import * as React from "react";
import { Field, FieldError, FieldLabel } from "@volca/ui/components/ui/field";
import { cn } from "@volca/ui/lib/utils";

export const AUTOCOMPLETE_INPUT_CLASS =
  "h-8 w-full min-w-0 flex-1 bg-transparent px-2.5 py-1 text-base outline-none placeholder:text-subtle disabled:cursor-not-allowed md:text-sm";

export const AUTOCOMPLETE_POPUP_CLASS =
  "absolute top-0 z-50 mt-1 w-full origin-top overflow-hidden rounded-lg bg-surface-overlay p-1 text-ink shadow-overlay outline-hidden duration-100 animate-in fade-in-0 zoom-in-95";

export const useCmdkInput = (
  inputRef: React.RefObject<HTMLInputElement | null>,
  labelledBy: string | undefined,
) => {
  const [inputId, setInputId] = React.useState<string>();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cmdk can replace the input ID after any commit; the equality guard prevents repeat updates
  React.useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    if (labelledBy) input.setAttribute("aria-labelledby", labelledBy);
    else input.removeAttribute("aria-labelledby");
    const next = input.id || undefined;
    if (next !== inputId) setInputId(next);
  });
  return inputId;
};

export const requiredPlaceholder = (
  placeholder: string | undefined,
  { label, required, disableRequirementAsterisk }: RequiredOptions,
) =>
  placeholder && required && !disableRequirementAsterisk && !label
    ? `${placeholder} *`
    : placeholder;

interface RequiredOptions {
  label?: string;
  required?: boolean;
  disableRequirementAsterisk?: boolean;
}

interface AutoCompleteFieldProps {
  label?: string;
  htmlFor?: string;
  labelId?: string;
  errorId?: string;
  required?: boolean;
  disableRequirementAsterisk?: boolean;
  error?: string;
  showErrorMessage?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

export const AutoCompleteField = ({
  label,
  htmlFor,
  labelId,
  required,
  disableRequirementAsterisk,
  errorId,
  error,
  showErrorMessage,
  disabled,
  fullWidth,
  children,
}: AutoCompleteFieldProps) => {
  const showAsterisk = Boolean(required && !disableRequirementAsterisk);

  return (
    <Field
      data-invalid={Boolean(error) || undefined}
      data-disabled={disabled || undefined}
      className={cn({ "w-auto": !fullWidth })}
    >
      {label && (
        <FieldLabel id={labelId} htmlFor={htmlFor} className="gap-0.5">
          {label}
          {showAsterisk && <span aria-hidden="true">*</span>}
        </FieldLabel>
      )}
      {children}
      {error && showErrorMessage && (
        <FieldError id={errorId}>{error}</FieldError>
      )}
    </Field>
  );
};
